import { api } from './api'
import { parseApiSuccessEnvelope, extractNestedResource } from '@/utils/apiEnvelope'
import { calendarDateKey } from '@/utils/calendarDates'
import { normalizeAppointment, unwrapGroupedAppointment } from '@/services/appointments/normalizeAppointment'

/** Valores de estado alineados con PATCH /appointments/:id/status y con el backend. */
const ALLOWED_STATUS = new Set(['pending', 'confirmed', 'cancelled', 'completed', 'no_show'])

/**
 * Estado válido para enviar en PUT o PATCH /status (solo valores permitidos).
 * @param {unknown} value
 * @param {string} [fallback]
 * @returns {string}
 */
function statusForPayload(value, fallback = 'pending') {
  if (value == null || value === '') return fallback
  const s = String(value).trim().toLowerCase().replace(/\s+/g, '_')
  if (ALLOWED_STATUS.has(s)) return s
  if (s === 'canceled') return 'cancelled'
  return fallback
}

/**
 * Maps the modal's references (each { clientId?, name?, phone? }) to the API
 * shape ([{ client_id?, full_name?, phone? }]). Returns null if empty.
 */
function normalizeReferencesPayload(references) {
  if (!Array.isArray(references)) return null
  const mapped = references
    .map((r) => {
      const out = {}
      const cid = r.clientId ?? r.client_id
      if (cid != null && cid !== '') out.client_id = cid
      const name = String(r.name ?? r.full_name ?? r.clientName ?? '').trim()
      if (name) out.full_name = name
      const phone = String(r.phone ?? '').trim()
      if (phone) out.phone = phone
      const wid = r.workerId ?? r.worker_id
      if (wid != null && wid !== '') out.worker_id = String(wid)
      return out
    })
    .filter((r) => r.client_id || r.full_name || r.phone)
  return mapped.length ? mapped : null
}

/** Lista de citas: array plano, envelope { data: [...] } o paginador Laravel { data: { data: [] } }. */
function extractAppointmentsList(response) {
  if (Array.isArray(response)) return response
  if (!response || typeof response !== 'object') return []
  const d = response.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object' && Array.isArray(d.data)) return d.data
  if (d && typeof d === 'object' && Array.isArray(d.appointments)) return d.appointments
  if (Array.isArray(response.appointments)) return response.appointments
  return []
}

/**
 * Metadatos de paginación. Soporta dos formatos:
 * - Laravel / meta anidado: `last_page` / `current_page`
 * - API gestor: `totalPages` / `page` / `hasNextPage`
 * @param {object} response
 * @returns {{ lastPage: number, currentPage: number }}
 */
function extractPaginationMeta(response) {
  if (!response || typeof response !== 'object') return { lastPage: 1, currentPage: 1 }
  const nested = response.data
  const inner = nested && typeof nested === 'object' && !Array.isArray(nested) ? nested : null
  const sources = [response.meta, inner?.meta, response, inner].filter((v) => v && typeof v === 'object')
  for (const src of sources) {
    const lp = src.last_page ?? src.lastPage ?? src.total_pages ?? src.totalPages
    const cp = src.current_page ?? src.currentPage ?? src.page
    const hasNext = src.has_next_page ?? src.hasNextPage
    if (lp != null || cp != null || hasNext != null) {
      const currentPage = Math.max(1, parseInt(String(cp), 10) || 1)
      let lastPage = lp != null ? Math.max(1, parseInt(String(lp), 10) || 1) : currentPage
      // Si la API solo informa hasNextPage, asegurar que haya al menos una página más.
      if (lp == null && hasNext === true) lastPage = currentPage + 1
      return { lastPage, currentPage }
    }
  }
  return { lastPage: 1, currentPage: 1 }
}

/**
 * Query GET /appointments: status, from_date, to_date, page, per_page (doc API).
 * @param {Record<string, string|number|undefined|null>} filters
 */
function buildAppointmentsQuery(filters) {
  const params = new URLSearchParams()
  if (filters.status != null && String(filters.status).trim() !== '') {
    params.set('status', String(filters.status).trim())
  }
  if (filters.from_date != null && String(filters.from_date).trim() !== '') {
    params.set('from_date', String(filters.from_date).trim().split('T')[0])
  }
  if (filters.to_date != null && String(filters.to_date).trim() !== '') {
    params.set('to_date', String(filters.to_date).trim().split('T')[0])
  }
  if (filters.worker_id != null && String(filters.worker_id).trim() !== '') {
    params.set('worker_id', String(filters.worker_id).trim())
  }
  if (filters.page != null && filters.page !== '') {
    params.set('page', String(filters.page))
  }
  if (filters.per_page != null && filters.per_page !== '') {
    params.set('per_page', String(filters.per_page))
  }
  return params.toString()
}

export const appointmentsService = {
  /**
   * Lista citas de la empresa activa (tenant en header).
   * Soporta filtros opcionales y recorre todas las páginas si la API pagina.
   * @param {object} [filters]
   * @param {string} [filters.status] pending | confirmed | completed | cancelled | no_show
   * @param {string} [filters.from_date] YYYY-MM-DD
   * @param {string} [filters.to_date] YYYY-MM-DD
   * @param {number} [filters.per_page] tamaño de página (default 100)
   */
  async getAll(filters = {}, options = {}) {
    try {
      const perPage =
        filters.per_page != null && String(filters.per_page).trim() !== ''
          ? Math.min(500, Math.max(1, parseInt(String(filters.per_page), 10) || 100))
          : 100
      const maxPages = 100
      const base = { ...filters }
      delete base.page
      delete base.per_page

      const all = []
      let page = 1
      let lastPage = 1

      for (;;) {
        const qs = buildAppointmentsQuery({ ...base, page, per_page: perPage })
        const endpoint = qs ? `/appointments?${qs}` : '/appointments'
        const response = await api.get(endpoint, true, { signal: options.signal })
        const items = extractAppointmentsList(response)
        all.push(...items.map(normalizeAppointment))
        const meta = extractPaginationMeta(response)
        lastPage = meta.lastPage
        if (page >= lastPage) break
        if (page >= maxPages) break
        page += 1
      }

      return all
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Error obteniendo citas:', error)
      }
      throw error
    }
  },

  /** GET /appointments/stats — resumen (roles worker-admin / admin según doc). */
  async getStats() {
    try {
      return await api.get('/appointments/stats', true)
    } catch (error) {
      console.error('Error obteniendo estadísticas de citas:', error)
      throw error
    }
  },

  /** GET /appointments/date/:date — citas del día (YYYY-MM-DD). */
  async getByDate(dateStr) {
    try {
      const key = String(dateStr || '').trim().split('T')[0]
      if (!key) return []
      const response = await api.get(`/appointments/date/${encodeURIComponent(key)}`, true)
      const list = extractAppointmentsList(response)
      return list.map(normalizeAppointment)
    } catch (error) {
      console.error('Error obteniendo citas por fecha:', error)
      throw error
    }
  },

  /*Obtiene una cita por su ID*/
  async getById(appointmentId) {
    try {
      const response = await api.get(`/appointments/${appointmentId}`, true)
      const raw = extractNestedResource(response) ?? response?.data ?? response
      return normalizeAppointment(raw && typeof raw === 'object' ? raw : {})
    } catch (error) {
      console.error('Error obteniendo cita:', error)
      throw error
    }
  },

  async getAvailability(dateStr, options = {}) {
    try {
      const date = String(dateStr || '').trim().split('T')[0]
      if (!date) return null

      const params = new URLSearchParams({ date })
      const slotStep = options.slotStep ?? options.slot_step
      if (slotStep != null && slotStep !== '') params.set('slot_step', String(slotStep))

      const serviceId = options.serviceId ?? options.service_id
      if (serviceId != null && serviceId !== '') params.set('service_id', String(serviceId))

      const excludeAppointmentId = options.excludeAppointmentId ?? options.exclude_appointment_id
      if (excludeAppointmentId != null && excludeAppointmentId !== '') {
        params.set('exclude_appointment_id', String(excludeAppointmentId))
      }

      const response = await api.get(`/appointments/availability?${params.toString()}`, true)
      return extractNestedResource(response) ?? response?.data ?? response
    } catch (error) {
      console.error('Error obteniendo disponibilidad de citas:', error)
      throw error
    }
  },

  /**
   * Crea cita (POST /appointments). Alcance por x-company-tenant-id; el estado lo asigna el backend.
   * @param {string|number} _companyId reservado por compatibilidad con el store (no se envía en el body)
   */
  async create(_companyId, appointmentData) {
    try {
      const payload = {
        service_id: String(appointmentData.serviceId),
        appointment_date: appointmentData.date,
        appointment_time: appointmentData.time,
        timezone: appointmentData.timezone || 'America/Mexico_City',
        notes: appointmentData.notes || ''
      }
      const cid = appointmentData.clientId
      if (cid != null && cid !== '') {
        payload.client_id = String(cid)
      }
      // Contacto: se envía SIEMPRE (haya o no client_id) para que el email tipeado se guarde en la cita.
      const name = (appointmentData.clientName || '').trim()
      if (name) payload.client_name = name
      const phone = (appointmentData.phone || appointmentData.clientPhone || '').trim()
      if (phone) payload.client_phone = phone
      const email = (appointmentData.email || appointmentData.clientEmail || '').trim()
      if (email) payload.client_email = email
      const wid = appointmentData.workerId ?? appointmentData.worker_id
      if (wid != null && wid !== '') {
        payload.worker_id = String(wid)
      }
      if (appointmentData.duration != null && appointmentData.duration !== '') {
        payload.duration = Number(appointmentData.duration)
      }
      if (appointmentData.price != null && appointmentData.price !== '') {
        payload.price = String(appointmentData.price)
      }
      const references = normalizeReferencesPayload(appointmentData.references)
      if (references) payload.references = references
      const { data, status } = await api.postWithStatus('/appointments', payload, true)
      const { success, message } = parseApiSuccessEnvelope(data)
      const raw = unwrapGroupedAppointment(extractNestedResource(data) ?? data?.data ?? data)
      const appointment =
        raw && typeof raw === 'object' ? normalizeAppointment(raw) : normalizeAppointment({})
      return {
        appointment,
        httpStatus: status,
        apiSuccess: success,
        apiMessage: message
      }
    } catch (error) {
      console.error('Error creando cita:', error)
      throw error
    }
  },

  /**
   * Actualiza cita (PUT /appointments/:id). Campos opcionales según API; tenant por header.
   * @param {string|number} _companyId compatibilidad store (no va en el body)
   */
  async update(appointmentId, _companyId, appointmentData) {
    try {
      const payload = {
        service_id: String(appointmentData.serviceId),
        appointment_date: appointmentData.date,
        appointment_time: appointmentData.time,
        price: String(appointmentData.price),
        notes: appointmentData.notes || ''
      }
      // duration DEBE ir como número: el backend hace aritmética (inicio + duración).
      // Enviarlo como string causaba "900" + "30" = "90030" → "no cabe en ningún bloque".
      if (appointmentData.duration != null && appointmentData.duration !== '') {
        payload.duration = Number(appointmentData.duration)
      }
      const cid = appointmentData.clientId
      if (cid != null && cid !== '') {
        payload.client_id = String(cid)
      }
      // Contacto del cliente: se envía para que la edición del email/teléfono persista.
      if (appointmentData.email !== undefined) {
        payload.client_email = String(appointmentData.email || '').trim()
      }
      const uName = (appointmentData.clientName || '').trim()
      if (uName) payload.client_name = uName
      const uPhone = (appointmentData.phone || appointmentData.clientPhone || '').trim()
      if (uPhone) payload.client_phone = uPhone
      // El usuario eligió explícitamente "actualizar cliente" en el modal de
      // confirmación al editar (ver AppointmentModal.vue) — propaga la
      // corrección al registro maestro del cliente, no solo al snapshot de la cita.
      if (appointmentData.syncClientRecord) payload.sync_client_record = true
      const wid = appointmentData.workerId ?? appointmentData.worker_id
      if (wid != null && wid !== '') {
        payload.worker_id = String(wid)
      }
      const hasStatus =
        appointmentData &&
        Object.prototype.hasOwnProperty.call(appointmentData, 'status') &&
        appointmentData.status != null &&
        String(appointmentData.status).trim() !== ''
      if (hasStatus) {
        payload.status = statusForPayload(appointmentData.status, 'pending')
      }
      if (appointmentData.references !== undefined) {
        payload.references = normalizeReferencesPayload(appointmentData.references) || []
      }
      const { data, status } = await api.putWithStatus(`/appointments/${appointmentId}`, payload, true)
      const { success, message } = parseApiSuccessEnvelope(data)
      const raw = unwrapGroupedAppointment(extractNestedResource(data) ?? data?.data ?? data)
      const appointment =
        raw && typeof raw === 'object' ? normalizeAppointment(raw) : normalizeAppointment({})
      return {
        appointment,
        httpStatus: status,
        apiSuccess: success,
        apiMessage: message
      }
    } catch (error) {
      console.error('Error actualizando cita:', error)
      throw error
    }
  },

  /**
   * Elimina o cancela cita (DELETE /appointments/:id).
   * @param {string|number} appointmentId
   * @param {{ permanent?: boolean }} [options] query opcional `permanent` según API
   */
  async delete(appointmentId, options = {}) {
    try {
      const q = options.permanent === true ? '?permanent=1' : ''
      const response = await api.delete(`/appointments/${appointmentId}${q}`, true)
      return response
    } catch (error) {
      console.error('Error eliminando cita:', error)
      throw error
    }
  },

  /**
   * Cambia el estado de una cita.
   * @param {string|number} appointmentId
   * @param {string} status
   * @param {number} [tipAmount] propina opcional (solo se persiste si `status` resulta en 'completed')
   */
  async updateStatus(appointmentId, status, tipAmount) {
    try {
      const body = { status: statusForPayload(status, 'pending') }
      if (tipAmount != null && tipAmount !== '') {
        body.tip_amount = Number(tipAmount)
      }
      const response = await api.patch(`/appointments/${appointmentId}/status`, body, true)
      return response
    } catch (error) {
      console.error('Error actualizando estado de cita:', error)
      throw error
    }
  },

  /**
   * Completa una cita y su orden de servicio vinculada en una sola operacion.
   * @param {string|number} appointmentId
   * @param {number} [tipAmount] propina opcional
   */
  async completeService(appointmentId, tipAmount) {
    try {
      const body = {}
      if (tipAmount != null && tipAmount !== '') {
        body.tip_amount = Number(tipAmount)
      }
      return await api.post(`/appointments/${appointmentId}/complete-service`, body, true)
    } catch (error) {
      throw error
    }
  },

  /** Cambia el estado de varias citas del mismo grupo (POST /appointments/group/:groupId/status). */
  async updateGroupStatus(groupId, appointmentIds, status, reason) {
    const body = { appointment_ids: appointmentIds, status: statusForPayload(status, 'cancelled') }
    if (reason) body.reason = reason
    return api.post(`/appointments/group/${encodeURIComponent(groupId)}/status`, body, true)
  },

  /** Mueve varias citas del mismo grupo a otra fecha/hora; 409 con `errors` si alguna choca. */
  async rescheduleGroup(groupId, appointmentIds, date, time) {
    return api.post(`/appointments/group/${encodeURIComponent(groupId)}/reschedule`, {
      appointment_ids: appointmentIds,
      appointment_date: date,
      appointment_time: time
    }, true)
  },

  /** Ids de clientes que ya tienen cita que se solapa con ese horario. */
  async getBookedClients(date, time, duration, excludeAppointmentId) {
    const params = new URLSearchParams({ date, time })
    if (duration) params.set('duration', String(duration))
    // Al editar, la propia cita no cuenta: sin esto su cliente salía "ya tiene una cita" contra sí mismo.
    if (excludeAppointmentId != null && excludeAppointmentId !== '') params.set('exclude_appointment_id', String(excludeAppointmentId))
    const response = await api.get(`/appointments/booked-clients?${params.toString()}`, true)
    const data = extractNestedResource(response) ?? response?.data ?? response
    const ids = Array.isArray(data?.client_ids) ? data.client_ids : []
    return ids.map((id) => String(id))
  },

  /**
   * Cuenta citas futuras pendientes/confirmadas de un trabajador (pre-fetch para offboarding, no autoritativo).
   * @param {string|number} workerId
   * @param {string|null} [timezone] IANA de la empresa, o null para la fecha local del dispositivo.
   */
  async countFutureByWorker(workerId, timezone = null) {
    try {
      const today = calendarDateKey(new Date(), timezone)
      const list = await this.getAll({ worker_id: workerId, from_date: today, status: 'pending,confirmed' })
      return list.length
    } catch (error) {
      console.error('Error contando citas futuras del trabajador:', error)
      throw error
    }
  }
}
